import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  EyeOutlined,
  ReloadOutlined,
  RocketOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useLanguage } from '../store/LanguageContext';
import { useAuth } from '../store/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toolSubmissionAPI } from '../services/api';

const { Title, Text } = Typography;

interface ToolSubmissionItem {
  id: number;
  tenant: 'CN' | 'GLOBAL';
  tool_name: string;
  tool_url: string;
  category?: string;
  description: string;
  availability_cn?: string;
  tags?: string;
  plan_type: 'free' | 'standard' | 'premium';
  contact_info?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'AUTO_EXITED';
  payment_status: 'PAID' | 'UNPAID';
  submission_time: string;
  review_deadline: string;
  approved_at?: string;
  review_note?: string;
  auto_approved: boolean;
  created_at: string;
  updated_at: string;
}

const planTag = {
  free: { color: 'green', zh: '免费', en: 'Free' },
  standard: { color: 'blue', zh: '标准', en: 'Standard' },
  premium: { color: 'gold', zh: '高级', en: 'Premium' },
};

const statusMeta = {
  PENDING: { color: 'processing' as const, zh: '待审核', en: 'Pending' },
  APPROVED: { color: 'success' as const, zh: '已通过', en: 'Approved' },
  REJECTED: { color: 'error' as const, zh: '已拒绝', en: 'Rejected' },
  AUTO_EXITED: { color: 'warning' as const, zh: '自动退出', en: 'Auto Exited' },
};

const AdminSubmissionsPage: React.FC = () => {
  const { language } = useLanguage();
  const isZh = language === 'zh-CN';
  const { user } = useAuth();
  const navigate = useNavigate();

  const [adminToken, setAdminToken] = useState('ADMIN_TOKEN');
  const [list, setList] = useState<ToolSubmissionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>();
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailItem, setDetailItem] = useState<ToolSubmissionItem | null>(null);

  const [editVisible, setEditVisible] = useState(false);
  const [editItem, setEditItem] = useState<ToolSubmissionItem | null>(null);
  const [editStatus, setEditStatus] = useState<ToolSubmissionItem['status']>('PENDING');
  const [editNote, setEditNote] = useState('');

  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, paid: 0 });

  useEffect(() => {
    if (!user || user.role !== 'admin') navigate('/');
  }, [user, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      params.limit = 300;

      const res = await toolSubmissionAPI.getAdminList(adminToken, params);
      let rows: ToolSubmissionItem[] = res.data?.submissions || [];

      if (keyword.trim()) {
        const kw = keyword.trim().toLowerCase();
        rows = rows.filter((item) =>
          [item.tool_name, item.tool_url, item.category, item.contact_info, item.review_note]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(kw))
        );
      }

      setList(rows);
      setPagination((prev) => ({ ...prev, total: rows.length }));

      setStats({
        total: rows.length,
        pending: rows.filter((x) => x.status === 'PENDING').length,
        approved: rows.filter((x) => x.status === 'APPROVED').length,
        paid: rows.filter((x) => x.payment_status === 'PAID').length,
      });
    } catch (error: any) {
      message.error(error?.response?.data?.error || (isZh ? '获取提交列表失败，请检查 Admin Token' : 'Failed to load submissions, check admin token'));
    } finally {
      setLoading(false);
    }
  }, [adminToken, statusFilter, keyword, isZh]);

  useEffect(() => {
    if (user?.role === 'admin') fetchData();
  }, [fetchData, user]);

  const openDetail = (item: ToolSubmissionItem) => {
    setDetailItem(item);
    setDetailVisible(true);
  };

  const openEdit = (item: ToolSubmissionItem) => {
    setEditItem(item);
    setEditStatus(item.status);
    setEditNote(item.review_note || '');
    setEditVisible(true);
  };

  const handleUpdateStatus = async () => {
    if (!editItem) return;
    try {
      await toolSubmissionAPI.updateAdminStatus(
        editItem.id,
        { status: editStatus, review_note: editNote || undefined },
        adminToken
      );
      message.success(isZh ? '状态更新成功' : 'Status updated');
      setEditVisible(false);
      fetchData();
    } catch (error: any) {
      message.error(error?.response?.data?.error || (isZh ? '更新失败' : 'Update failed'));
    }
  };

  const columns: ColumnsType<ToolSubmissionItem> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70,
    },
    {
      title: isZh ? '工具' : 'Tool',
      dataIndex: 'tool_name',
      width: 210,
      render: (_v, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.tool_name}</Text>
          <a href={row.tool_url} target="_blank" rel="noreferrer">{isZh ? '打开链接' : 'Open'}</a>
        </Space>
      ),
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant',
      width: 90,
      render: (v: ToolSubmissionItem['tenant']) => <Tag>{v}</Tag>,
    },
    {
      title: isZh ? '套餐' : 'Plan',
      dataIndex: 'plan_type',
      width: 110,
      render: (v: ToolSubmissionItem['plan_type']) => (
        <Tag color={planTag[v].color}>{isZh ? planTag[v].zh : planTag[v].en}</Tag>
      ),
    },
    {
      title: isZh ? '支付' : 'Payment',
      dataIndex: 'payment_status',
      width: 100,
      render: (v: ToolSubmissionItem['payment_status']) => (
        <Tag color={v === 'PAID' ? 'green' : 'orange'}>{v === 'PAID' ? (isZh ? '已支付' : 'Paid') : (isZh ? '未支付' : 'Unpaid')}</Tag>
      ),
    },
    {
      title: isZh ? '审核状态' : 'Review',
      dataIndex: 'status',
      width: 130,
      render: (v: ToolSubmissionItem['status']) => (
        <Badge status={statusMeta[v].color} text={isZh ? statusMeta[v].zh : statusMeta[v].en} />
      ),
    },
    {
      title: isZh ? '提交时间' : 'Submitted',
      dataIndex: 'submission_time',
      width: 170,
      render: (v: string) => new Date(v).toLocaleString(isZh ? 'zh-CN' : 'en-US'),
    },
    {
      title: isZh ? '截止时间' : 'Deadline',
      dataIndex: 'review_deadline',
      width: 170,
      render: (v: string) => new Date(v).toLocaleString(isZh ? 'zh-CN' : 'en-US'),
    },
    {
      title: isZh ? '操作' : 'Actions',
      key: 'actions',
      width: 170,
      render: (_v, row) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(row)} />
          <Button size="small" icon={<CheckCircleOutlined />} onClick={() => openEdit(row)} />
        </Space>
      ),
    },
  ];

  const currentData = list.slice((pagination.page - 1) * pagination.pageSize, pagination.page * pagination.pageSize);

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <Title level={3} style={{ marginBottom: 20 }}>
        <RocketOutlined /> {isZh ? '工具提交审核（新流程）' : 'Tool Submissions (New Flow)'}
      </Title>

      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            value={adminToken}
            onChange={(e) => setAdminToken(e.target.value)}
            placeholder="x-admin-token"
          />
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder={isZh ? '搜索工具名/URL/联系方式' : 'Search tool/url/contact'}
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col xs={24} md={6}>
              <Select
                allowClear
                style={{ width: '100%' }}
                value={statusFilter}
                onChange={(v) => setStatusFilter(v)}
                placeholder={isZh ? '筛选状态' : 'Filter status'}
                options={[
                  { label: isZh ? '待审核' : 'Pending', value: 'PENDING' },
                  { label: isZh ? '已通过' : 'Approved', value: 'APPROVED' },
                  { label: isZh ? '已拒绝' : 'Rejected', value: 'REJECTED' },
                  { label: isZh ? '自动退出' : 'Auto Exited', value: 'AUTO_EXITED' },
                ]}
              />
            </Col>
            <Col xs={24} md={6}>
              <Button icon={<ReloadOutlined />} onClick={fetchData}>
                {isZh ? '刷新' : 'Refresh'}
              </Button>
            </Col>
          </Row>
        </Space>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}><Card><Statistic title={isZh ? '总提交' : 'Total'} value={stats.total} prefix={<RocketOutlined />} /></Card></Col>
        <Col xs={12} sm={6}><Card><Statistic title={isZh ? '待审核' : 'Pending'} value={stats.pending} prefix={<ClockCircleOutlined />} /></Card></Col>
        <Col xs={12} sm={6}><Card><Statistic title={isZh ? '已通过' : 'Approved'} value={stats.approved} prefix={<CheckCircleOutlined />} /></Card></Col>
        <Col xs={12} sm={6}><Card><Statistic title={isZh ? '已支付' : 'Paid'} value={stats.paid} prefix={<DollarOutlined />} /></Card></Col>
      </Row>

      <Card>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={currentData}
          pagination={{
            current: pagination.page,
            total: pagination.total,
            pageSize: pagination.pageSize,
            onChange: (page, pageSize) => setPagination((prev) => ({ ...prev, page, pageSize: pageSize || 20 })),
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={820}
        title={isZh ? '提交详情' : 'Submission Detail'}
      >
        {detailItem && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="ID">{detailItem.id}</Descriptions.Item>
            <Descriptions.Item label="Tenant">{detailItem.tenant}</Descriptions.Item>
            <Descriptions.Item label={isZh ? '工具名称' : 'Tool'}>{detailItem.tool_name}</Descriptions.Item>
            <Descriptions.Item label="URL">
              <a href={detailItem.tool_url} target="_blank" rel="noreferrer">{detailItem.tool_url}</a>
            </Descriptions.Item>
            <Descriptions.Item label={isZh ? '套餐' : 'Plan'}>{detailItem.plan_type}</Descriptions.Item>
            <Descriptions.Item label={isZh ? '支付' : 'Payment'}>{detailItem.payment_status}</Descriptions.Item>
            <Descriptions.Item label={isZh ? '审核状态' : 'Status'}>{detailItem.status}</Descriptions.Item>
            <Descriptions.Item label={isZh ? '自动通过' : 'Auto Approved'}>{detailItem.auto_approved ? 'Yes' : 'No'}</Descriptions.Item>
            <Descriptions.Item label={isZh ? '截止审核' : 'Deadline'}>{new Date(detailItem.review_deadline).toLocaleString(isZh ? 'zh-CN' : 'en-US')}</Descriptions.Item>
            <Descriptions.Item label={isZh ? '联系方式' : 'Contact'}>{detailItem.contact_info || '-'}</Descriptions.Item>
            <Descriptions.Item label={isZh ? '可用性' : 'Availability'} span={2}>{detailItem.availability_cn || '-'}</Descriptions.Item>
            <Descriptions.Item label="Tags" span={2}>{detailItem.tags || '-'}</Descriptions.Item>
            <Descriptions.Item label={isZh ? '描述' : 'Description'} span={2}>{detailItem.description}</Descriptions.Item>
            <Descriptions.Item label={isZh ? '审核备注' : 'Review Note'} span={2}>{detailItem.review_note || '-'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        onOk={handleUpdateStatus}
        okText={isZh ? '保存' : 'Save'}
        title={isZh ? '更新审核状态' : 'Update Review Status'}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Select
            value={editStatus}
            onChange={(v) => setEditStatus(v)}
            options={[
              { value: 'PENDING', label: isZh ? '待审核' : 'Pending' },
              { value: 'APPROVED', label: isZh ? '已通过' : 'Approved' },
              { value: 'REJECTED', label: isZh ? '已拒绝' : 'Rejected' },
              { value: 'AUTO_EXITED', label: isZh ? '自动退出' : 'Auto Exited' },
            ]}
          />
          <Input.TextArea
            rows={4}
            value={editNote}
            onChange={(e) => setEditNote(e.target.value)}
            placeholder={isZh ? '审核备注（可选）' : 'Review note (optional)'}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default AdminSubmissionsPage;
